// src/components/TrainingInterface.js
"use client";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
   AppBar, Toolbar, IconButton, Container, TextField, Button, Typography, Box, List,
   ListItem, ListItemText, Checkbox, CircularProgress, Backdrop, Paper, Divider, Chip,
   Dialog, DialogTitle, DialogContent, DialogActions, LinearProgress, Stack,
   InputAdornment, Stepper, Step, StepLabel, Tooltip, Grid, Popover, Alert, Pagination,
   Accordion, AccordionSummary, AccordionDetails, Fab, Badge, useMediaQuery,
   Link as MUILink, Switch, FormControlLabel
 } from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import { toast } from "react-toastify";
import axios from "axios";

import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ClearIcon from "@mui/icons-material/Clear";
import DescriptionIcon from "@mui/icons-material/Description";
import SearchIcon from "@mui/icons-material/Search";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import PendingIcon from "@mui/icons-material/Pending";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import CloseIcon from "@mui/icons-material/Close";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import BoltIcon from "@mui/icons-material/Bolt";
import LinkIcon from "@mui/icons-material/Link";
import LabelIcon from "@mui/icons-material/Label";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import OpenInFullIcon from "@mui/icons-material/OpenInFull";
import CloseFullscreenIcon from "@mui/icons-material/CloseFullscreen";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import VisibilityIcon from "@mui/icons-material/Visibility";
import LockIcon from "@mui/icons-material/Lock";

// NEW icons for file types
import AudiotrackIcon from "@mui/icons-material/Audiotrack";
import MovieIcon from "@mui/icons-material/Movie";
import ImageIcon from "@mui/icons-material/Image";

import ThemeToggleButton from "./ThemeToggleButton";
import DocumentViewer from "./DocumentViewer";
import UniversalDocumentViewer from "./UniversalDocumentViewer";

import AdvancedIntegrationsSection from "./AdvancedIntegrationsSection";


const API_BASE = process.env.REACT_APP_API;
const AUTO_TRAIN = false;
const USE_ASYNC_TRAIN = true;

// Poll cadence & timeout for /train-status
const TRAIN_STATUS_POLL_MS = 2000;
const TRAIN_STATUS_TIMEOUT_MS = 15 * 60 * 1000;

const isInstructionKey = (key = "") => {
  const k = String(key).toLowerCase();
  return (
    k.endsWith("/prompts.txt") ||
    k.endsWith("/prompts.json") ||
    k.endsWith("prompts.txt") ||
    k.endsWith("prompts.json")
  );
};

// Heuristic: keys for URL ingests are under ".../urls/..."
const isUrlKey = (key = "") => String(key).toLowerCase().includes("/urls/");

// ---- File kind helpers ----
const getExt = (s = "") => {
  const m = String(s).toLowerCase().match(/\.([a-z0-9]+)(\?|$)/);
  return m ? m[1] : "";
};
const AUDIO_EXTS = new Set(["mp3","wav","m4a","aac","flac","oga","ogg","opus","wma","aiff","alac"]);
const VIDEO_EXTS = new Set(["mp4","mov","m4v","webm","mkv","avi","wmv","flv","mpeg","mpg","3gp","mxf"]);
const IMAGE_EXTS = new Set(["png","jpg","jpeg","gif","webp","bmp","tif","tiff","heic","svg","avif"]);

const getKindFromExt = (nameOrKey="") => {
  const ext = getExt(nameOrKey);
  if (AUDIO_EXTS.has(ext)) return "audio";
  if (VIDEO_EXTS.has(ext)) return "video";
  if (IMAGE_EXTS.has(ext)) return "image";
  return "doc";
};
const KindIcon = ({ kind="doc" }) => {
  if (kind === "audio") return <AudiotrackIcon sx={{ mr: 1, color: "text.secondary" }} />;
  if (kind === "video") return <MovieIcon sx={{ mr: 1, color: "text.secondary" }} />;
  if (kind === "image") return <ImageIcon sx={{ mr: 1, color: "text.secondary" }} />;
  return <DescriptionIcon sx={{ mr: 1, color: "text.secondary" }} />;
};
const kindLabel = (k) => (k==="audio" ? "Audio" : k==="video" ? "Video" : k==="image" ? "Image" : "Document");

// ---- URL helpers ----
const isYouTubeUrl = (u = "") => {
  try {
    const x = new URL(String(u).trim());
    const host = x.hostname.toLowerCase();
    return host.includes("youtube.com") || host === "youtu.be";
  } catch { return false; }
};

const AssistantStatusChip = ({ anyTrained, hasChangesToTrain, isTraining }) => {
  if (hasChangesToTrain) {
    return (
      <Chip
        size="small"
        color="warning"
        label={isTraining ? "Training…" : "Changes pending training"}
      />
    );
  }
  if (anyTrained) {
    return <Chip size="small" color="success" label="Assistant trained" />;
  }
  return <Chip size="small" variant="outlined" label="Not trained" />;
};

// protected files
const PROTECTED_FILE_NAMES = new Set(["initialtraining.pdf"]);
const isProtectedKey = (key = "", fileMetaByKey = {}) => {
  const meta = fileMetaByKey[key] || {};
  const name = String(meta?.Name || key.split("/").pop() || "").toLowerCase();
  return Boolean(meta?.Protected || meta?.Owner === "admin" || PROTECTED_FILE_NAMES.has(name));
};


/* ---------- Tiny Coachmark ---------- */
function Coachmark({ open, anchorEl, title, body, step, total, onNext, onPrev, onSkip, onDone }) {
  const [fallbackPos] = React.useState({
    top: typeof window !== "undefined" ? Math.round(window.innerHeight * 0.25) : 200,
    left: typeof window !== "undefined" ? Math.round(window.innerWidth / 2) : 300,
  });

  return (
    <>
      <Backdrop open={open} sx={{ zIndex: (t) => t.zIndex.modal - 2, bgcolor: "rgba(0,0,0,0.35)" }} />
      <Popover
        open={open}
        anchorEl={anchorEl || undefined}
        anchorReference={anchorEl ? "anchorEl" : "anchorPosition"}
        anchorPosition={fallbackPos}
        onClose={onSkip}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        transformOrigin={{ vertical: "top", horizontal: "center" }}
        PaperProps={{ sx: { p: 2, borderRadius: 2, maxWidth: 360, boxShadow: 6 } }}
      >
        <Stack spacing={1}>
          <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>{title}</Typography>
          <Typography variant="body2" sx={{ color: "text.secondary" }}>{body}</Typography>
          <Stack direction="row" spacing={1} justifyContent="space-between" alignItems="center" sx={{ pt: 1 }}>
            <Typography variant="caption" sx={{ color: "text.secondary" }}>
              Step {step + 1} of {total}
            </Typography>
            <Stack direction="row" spacing={1}>
              <Button size="small" onClick={onSkip}>Skip</Button>
              {step > 0 && <Button size="small" onClick={onPrev}>Back</Button>}
              {step < total - 1 ? (
                <Button size="small" variant="contained" onClick={onNext}>Next</Button>
              ) : (
                <Button size="small" variant="contained" onClick={onDone}>Done</Button>
              )}
            </Stack>
          </Stack>
        </Stack>
      </Popover>
    </>
  );
}

/* ---------- Reusable Tag input (chips) ---------- */
function TagInput({ value = [], onChange, placeholder = "Add a tag and press Enter", size = "small", disabled = false }) {
  const [draft, setDraft] = useState("");
  const addTag = (t) => {
    const s = (t || "").trim();
    if (!s) return;
    const clean = s.slice(0, 32).replace(/[^a-zA-Z0-9\-_ ]/g, "").replace(/\s{2,}/g, " ").trim();
    if (!clean) return;
    const next = Array.from(new Set([...(value || []), clean]));
    onChange?.(next.slice(0, 10));
    setDraft("");
  };
  const remove = (t) => onChange?.((value || []).filter((x) => x !== t));
  const onKey = (e) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      addTag(draft);
    }
  };
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
      <TextField
        size={size}
        disabled={disabled}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={onKey}
        placeholder={placeholder}
        InputProps={{ startAdornment: (<InputAdornment position="start"><LabelIcon fontSize="small" /></InputAdornment>) }}
        sx={{ minWidth: 220 }}
      />
      <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
        {(value || []).map((t, i) => (
          <Chip key={`${t}-${i}`} label={t} onDelete={() => remove(t)} size="small" />
        ))}
      </Stack>
    </Box>
  );
}

/* ---------- Plan upgrade dialog shown on 402 ---------- */
function PlanLimitDialog({ open, onClose, reason }) {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Upgrade required</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" sx={{ mb: 1.5 }}>
          {reason || "You’ve reached the limit for your current plan."}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Upgrade to continue uploading files, adding URLs, or training larger assistants.
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        <Button
          variant="contained"
          onClick={() => { window.location.href = "/billing"; }}
        >
          View plans
        </Button>
      </DialogActions>
    </Dialog>
  );
}

const TrainingInterface = ({
  assistantId = "",
  onAssistantReady,
  onGoToChat,
  hideHeader = false,
  onStatusChange = () => {},
  isPremium = false,
  userPlan,
}) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("md"));
  // Auto-train UI state (defaults to constant AUTO_TRAIN = false)
  const [autoTrain, setAutoTrain] = useState(Boolean(AUTO_TRAIN));
  const [autoInfoEl, setAutoInfoEl] = useState(null);

  // Files & meta
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [fileMetaByKey, setFileMetaByKey] = useState({});

  // Snapshot of last trained state
  const [trainedSnapshotKeys, setTrainedSnapshotKeys] = useState([]);

  // Assistant details
  const [assistantName, setAssistantName] = useState("");
  const [assistantDesc, setAssistantDesc] = useState("");
  const [isEditingDetails, setIsEditingDetails] = useState(false);
  const [draftName, setDraftName] = useState("");
  const [draftDesc, setDraftDesc] = useState("");
  const [savingDetails, setSavingDetails] = useState(false);

  // Selections
  const [checkedFiles, setCheckedFiles] = useState([]);

  // Instructions
  const [editorText, setEditorText] = useState("");
  const [loadingInstructions, setLoadingInstructions] = useState(false);
  const [savingInstructions, setSavingInstructions] = useState(false);
  const [forcePendingSinceSave, setForcePendingSinceSave] = useState(false);
  const [instructionsModalOpen, setInstructionsModalOpen] = useState(false);

  // Uploads (with tag modal)
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploadProgress, setUploadProgress] = useState({});
  const [selectedFileTags, setSelectedFileTags] = useState({}); // { "file.pdf": ["tag1"] }

  // Tagging modal
  const [tagModalOpen, setTagModalOpen] = useState(false);

  // URL ingestion (per-URL tagging)
  const [urlInput, setUrlInput] = useState("");
  const [urlEntries, setUrlEntries] = useState([]); // [{id,url,tags:[]}]
  const [isAddingUrls, setIsAddingUrls] = useState(false);

  // Fetch/training state
  const [isUploading, setIsUploading] = useState(false);
  const [isFetching, setIsFetching] = useState(false);
  const [isTraining, setIsTraining] = useState(false);
  const [isBootstrapping, setIsBootstrapping] = useState(false);

  // Search queries
  const [fileQuery, setFileQuery] = useState("");
  const [urlQuery, setUrlQuery] = useState("");

  const [hasChangesToTrain, setHasChangesToTrain] = useState(false);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);

  // Edit tags dialog
  const [tagEditOpen, setTagEditOpen] = useState(false);
  const [tagEditKey, setTagEditKey] = useState("");
  const [tagEditValue, setTagEditValue] = useState([]);

  // 402 / upgrade dialog
  const [planDialogOpen, setPlanDialogOpen] = useState(false);
  const [planDialogReason, setPlanDialogReason] = useState("");

  // Viewer state
  const [viewerOpen, setViewerOpen] = useState(false);
  const [viewer, setViewer] = useState({ url: "", name: "", contentType: "" });

  // Refs for tour anchors
  const uploadRef = useRef(null);
  const filesRef = useRef(null);
  const instructionsRef = useRef(null);
  const trainFabRef = useRef(null);

  // Tour state
  const [tour, setTour] = useState({ open: false, step: 0 });

  // ===== ASYNC TRAIN STATE =====
  const [trainJobId, setTrainJobId] = useState(null);
  const [trainStatus, setTrainStatus] = useState(null); // queued | processing | completed | completed_with_errors | failed
  const [trainStatusError, setTrainStatusError] = useState("");
  const trainPollHandleRef = useRef(null);
  const trainStartedAtRef = useRef(null);

  const startTour = () => setTour({ open: true, step: 0 });
  const endTour = () => setTour({ open: false, step: 0 });

  // ===== ASYNC TRAIN HELPERS =====
  const clearTrainPolling = () => {
    if (trainPollHandleRef.current) {
      clearInterval(trainPollHandleRef.current);
      trainPollHandleRef.current = null;
    }
  };

  const stopTrainingUI = () => {
    clearTrainPolling();
    setIsTraining(false);
    setTrainJobId(null);
  };

  useEffect(() => {
    if (!assistantId) return;
    startTour();
  }, [assistantId]);

  // UPDATED tour copy for new flow
  const tourSteps = [
    { ref: uploadRef, title: "Add files fast", body: "Choose files, add optional tags in the popup, and they’ll upload automatically." },
    { ref: filesRef, title: "Review & manage", body: "Browse Files/URLs, edit tags, delete items, and open files or source links." },
    { ref: instructionsRef, title: "Write instructions", body: "Save your instructions to enable training." },
    { ref: trainFabRef, title: "Train from anywhere", body: "Use this floating button when you see pending changes." },
  ];

  useEffect(() => {
    if (!tour.open) return;
    const el = tourSteps[tour.step]?.ref?.current;
    if (el && "scrollIntoView" in el) setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "center" }), 80);
  }, [tour.open, tour.step]);

  useEffect(() => {
    if (!tour.open) return;
    const onKey = (e) => { if (e.key === "Escape") endTour(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [tour.open]);

  const anyTrained = useMemo(() => trainedSnapshotKeys.length > 0, [trainedSnapshotKeys]);

  const instructionKey = useMemo(() => {
    const jsonKey = (uploadedFiles || []).find((k) => isInstructionKey(k) && k.toLowerCase().endsWith("prompts.json"));
    if (jsonKey) return jsonKey;
    const txtKey = (uploadedFiles || []).find((k) => isInstructionKey(k) && k.toLowerCase().endsWith("prompts.txt"));
    return txtKey || null;
  }, [uploadedFiles]);

  const instructionMeta = instructionKey ? fileMetaByKey[instructionKey] : null;
  const instructionUsed = !!(instructionMeta && instructionMeta.UsedInTrainingAt);
  const instructionPending = forcePendingSinceSave || (instructionKey ? !instructionUsed : false);

  const anyFilePending = useMemo(() => {
    const metas = Object.values(fileMetaByKey || {});
    return metas.length > 0 && metas.some((m) => !m?.UsedInTrainingAt);
  }, [fileMetaByKey]);

  const hasInstructionFile = !!instructionKey;

  const nonInstructionKeys = useMemo(
    () => (uploadedFiles || []).filter((k) => !isInstructionKey(k)),
    [uploadedFiles]
  );

  // Split into docs vs urls
  const docKeys = useMemo(() => nonInstructionKeys.filter((k) => !isUrlKey(k)), [nonInstructionKeys]);
  const urlKeys = useMemo(() => nonInstructionKeys.filter((k) => isUrlKey(k)), [nonInstructionKeys]);

  // Filtered by search (name, key, or tags)
  const filteredDocs = useMemo(() => {
    const q = fileQuery.trim().toLowerCase();
    if (!q) return docKeys;
    return docKeys.filter((k) =>
      k.toLowerCase().includes(q) ||
      (fileMetaByKey[k]?.Name || "").toLowerCase().includes(q) ||
      (fileMetaByKey[k]?.Tags || []).join(" ").toLowerCase().includes(q)
    );
  }, [docKeys, fileQuery, fileMetaByKey]);

  const filteredUrls = useMemo(() => {
    const q = urlQuery.trim().toLowerCase();
    if (!q) return urlKeys;
    return urlKeys.filter((k) =>
      k.toLowerCase().includes(q) ||
      (fileMetaByKey[k]?.Name || "").toLowerCase().includes(q) ||
      (fileMetaByKey[k]?.Tags || []).join(" ").toLowerCase().includes(q)
    );
  }, [urlKeys, urlQuery, fileMetaByKey]);

  // Pagination
  const FILES_PER_PAGE = 5;
  const [filesPage, setFilesPage] = useState(1);
  const filesTotal = filteredDocs.length;
  const filesPageCount = Math.max(1, Math.ceil(filesTotal / FILES_PER_PAGE));
  const filesStart = (filesPage - 1) * FILES_PER_PAGE;
  const filesEnd = Math.min(filesStart + FILES_PER_PAGE, filesTotal);
  const pageDocKeys = filteredDocs.slice(filesStart, filesEnd);

  useEffect(() => { setFilesPage(1); }, [fileQuery]);
  useEffect(() => {
    const maxPage = Math.max(1, Math.ceil(filteredDocs.length / FILES_PER_PAGE));
    if (filesPage > maxPage) setFilesPage(maxPage);
  }, [filteredDocs.length, filesPage]);

  const [urlsPage, setUrlsPage] = useState(1);
  const urlsTotal = filteredUrls.length;
  const urlsPageCount = Math.max(1, Math.ceil(urlsTotal / FILES_PER_PAGE));
  const urlsStart = (urlsPage - 1) * FILES_PER_PAGE;
  const urlsEnd = Math.min(urlsStart + FILES_PER_PAGE, urlsTotal);
  const pageUrlKeys = filteredUrls.slice(urlsStart, urlsEnd);

  useEffect(() => { setUrlsPage(1); }, [urlQuery]);
  useEffect(() => {
    const maxPage = Math.max(1, Math.ceil(filteredUrls.length / FILES_PER_PAGE));
    if (urlsPage > maxPage) setUrlsPage(maxPage);
  }, [filteredUrls.length, urlsPage]);

  const selectableDocKeys = pageDocKeys.filter((k) => !isProtectedKey(k, fileMetaByKey));
  const selectableUrlKeys = pageUrlKeys.filter((k) => !isProtectedKey(k, fileMetaByKey));

  const filesPageAllChecked = selectableDocKeys.length > 0 && selectableDocKeys.every((k) => checkedFiles.includes(k));
  const urlsPageAllChecked = selectableUrlKeys.length > 0 && selectableUrlKeys.every((k) => checkedFiles.includes(k));

  const toggleFilesPage = () => {
    if (filesPageAllChecked) {
      setCheckedFiles((prev) => prev.filter((k) => !selectableDocKeys.includes(k)));
    } else {
      setCheckedFiles((prev) => Array.from(new Set([...prev, ...selectableDocKeys])));
    }
  };

  const toggleUrlsPage = () => {
    if (urlsPageAllChecked) {
      setCheckedFiles((prev) => prev.filter((k) => !selectableUrlKeys.includes(k)));
    } else {
      setCheckedFiles((prev) => Array.from(new Set([...prev, ...selectableUrlKeys])));
    }
  };

  const activeStep = useMemo(() => {
    if (isTraining) return 1;
    if ((uploadedFiles?.length || 0) === 0) return 0;
    if (anyTrained && !hasChangesToTrain) return 2;
    return 1;
  }, [isTraining, uploadedFiles, hasChangesToTrain, anyTrained]);

  const detailsChanged = useMemo(
    () => draftName.trim() !== assistantName || draftDesc.trim() !== assistantDesc,
    [draftName, draftDesc, assistantName, assistantDesc]
  );

  const computeHasChanges = useMemo(() => {
    const current = new Set(uploadedFiles);
    const trained = new Set(trainedSnapshotKeys);
    const setDiff =
      current.size !== trained.size
        ? current.size + trained.size > 0
        : [...current].some((k) => !trained.has(k));
    return Boolean(setDiff || instructionPending || anyFilePending || forcePendingSinceSave);
  }, [uploadedFiles, trainedSnapshotKeys, instructionPending, anyFilePending, forcePendingSinceSave]);

  useEffect(() => { setHasChangesToTrain(computeHasChanges); }, [computeHasChanges]);
  useEffect(() => {
    onStatusChange({ isTraining, isFetching, hasChangesToTrain, anyTrained });
  }, [isTraining, isFetching, hasChangesToTrain, anyTrained, onStatusChange]);

  const pendingCount = useMemo(() => {
    let count = 0;
    Object.values(fileMetaByKey || {}).forEach((m) => { if (!m?.UsedInTrainingAt) count += 1; });
    if (instructionPending) count += 1;
    return count;
  }, [fileMetaByKey, instructionPending]);

  const isLikelyUrl = (s = "") => {
    try {
      const u = new URL(String(s).trim());
      return u.protocol === "http:" || u.protocol === "https:";
    } catch { return false; }
  };

  // ---------- API ----------
  const fetchAssistantMeta = async () => {
    if (!assistantId) return null;
    try {
      const res = await axios.get(`${API_BASE}/assistants/${assistantId}`);
      const uploads = (res.data && res.data.Uploads) || [];
      const byKey = {};
      uploads.forEach((u) => { byKey[u.Key] = u; });
      setFileMetaByKey(byKey);
      setUploadedFiles(uploads.map((u) => u.Key));
      setTrainedSnapshotKeys(uploads.filter((u) => u.UsedInTrainingAt).map((u) => u.Key));
      setAssistantName(res.data?.Name || "");
      setAssistantDesc(res.data?.Description || "");
      setDraftName(res.data?.Name || "");
      setDraftDesc(res.data?.Description || "");
      setIsEditingDetails(false);
      return res.data;
    } catch {
      setFileMetaByKey({});
      setUploadedFiles([]);
      setTrainedSnapshotKeys([]);
      setAssistantName("");
      setAssistantDesc("");
      setDraftName("");
      setDraftDesc("");
      setIsEditingDetails(false);
      return null;
    }
  };

  const fetchInstructionsText = async () => {
    if (!assistantId) return;
    setLoadingInstructions(true);
    try {
      const res = await axios.get(`${API_BASE}/assistants/${assistantId}/instructions`);
      setEditorText(res?.data?.text ?? "");
    } catch {} finally { setLoadingInstructions(false); }
  };

  const fetchAssistantFiles = async () => {
    if (!assistantId) return [];
    try {
      const res = await axios.get(`${API_BASE}/assistants/${assistantId}/files`);
      return res.data || [];
    } catch { return []; }
  };

  const refreshAll = async () => {
    setIsFetching(true);
    await Promise.all([fetchAssistantMeta(), fetchAssistantFiles()]);
    await fetchInstructionsText();
    setIsFetching(false);
  };

  useEffect(() => {
    if (!assistantId) return;
    let mounted = true;
    (async () => {
      setIsBootstrapping(true);
      await refreshAll();
      if (mounted) setIsBootstrapping(false);
    })();
    return () => { mounted = false; };
  }, [assistantId]);

  const saveDetails = async () => {
    if (!assistantId) return;
    setSavingDetails(true);
    try {
      await axios.put(`${API_BASE}/assistants/${assistantId}`, {
        name: draftName.trim(),
        description: draftDesc.trim(),
      });
      setAssistantName(draftName.trim());
      setAssistantDesc(draftDesc.trim());
      toast.success("Assistant details updated");
      setIsEditingDetails(false);
    } catch {
      toast.error("Failed to update assistant details");
    } finally {
      setSavingDetails(false);
    }
  };

  const needsServerPreview = (ct = "", key = "") => {
    const k = key.toLowerCase();
    const badExt = /\.(pptx?|doc(?!x)|odt|rtf|odp|odg|pages|numbers|key|psd|ai|eps)(\?|$)/i.test(k);
    if (badExt) return true;
    const s = String(ct || "").toLowerCase();
    if (/application\/vnd\.ms-powerpoint|application\/vnd\.openxmlformats-officedocument\.presentationml\.presentation/.test(s)) return true;
    if (/application\/msword/.test(s)) return true;
    return false;
  };

  const openViewerForKey = async (key) => {
    try {
      const name = (fileMetaByKey[key]?.Name) || key.split("/").pop();

      // 1) Ask backend for presigned URL and mime
      const { data, status } = await axios.get(
        `${API_BASE}/assistants/${assistantId}/files/presigned`,
        { params: { key, filename: name }, validateStatus: () => true }
      );
      if (status === 402) { setPlanDialogReason(data?.error || "Plan limit reached."); setPlanDialogOpen(true); return; }
      if (status !== 200 || !data?.url) { toast.error("Failed to get view link"); return; }

      const { url, content_type } = data;

      // 2) If browser-friendly, show directly
      if (!needsServerPreview(content_type, key)) {
        setViewer({ url, name, contentType: content_type || "" });
        setViewerOpen(true);
        return;
      }

      // 3) Else call preview API
      const p = await axios.post(
        `${API_BASE}/assistants/${assistantId}/preview`,
        { key },
        { validateStatus: () => true }
      );
      if (p.status === 202 && p.data?.job_id) {
        const jobId = p.data.job_id;
        let tries = 0;
        const maxTries = 60;
        const tick = async () => {
          tries += 1;
          const st = await axios.get(
            `${API_BASE}/assistants/${assistantId}/preview-status`,
            { params: { job_id: jobId }, validateStatus: () => true }
          );
          if (st.status !== 200) { if (tries < maxTries) return setTimeout(tick, 2000); toast.error("Preview failed"); return; }
          const body = st.data || {};
          if (body.status === "ready" && body.preview_url) {
            setViewer({ url: body.preview_url, name: name + (body.preview_ext || ""), contentType: body.preview_content_type || "application/pdf" });
            setViewerOpen(true);
            return;
          }
          if (body.status === "failed") { toast.error(body.error || "Preview failed"); return; }
          if (tries < maxTries) setTimeout(tick, 2000);
          else toast.error("Preview timed out");
        };
        tick();
        return;
      } else if (p.status === 200 && p.data?.preview_url) {
        setViewer({ url: p.data.preview_url, name: name + (p.data.preview_ext || ""), contentType: p.data.preview_content_type || "application/pdf" });
        setViewerOpen(true);
        return;
      } else {
        toast.error(p.data?.error || "Unable to start preview");
      }
    } catch {
      toast.error("Failed to view file");
    }
  };

  // ---------- Uploads with tag modal ----------
  const uploadSingleFile = async (file) => {
    setIsUploading(true);
    setUploadProgress((p) => ({ ...p, [file.name]: 0 }));
    const tags = selectedFileTags[file.name] || [];
    try {
      const form = new FormData();
      form.append("file", file);
      form.append("overwrite", "true");
      if (Array.isArray(tags) && tags.length) {
        form.append("tags", JSON.stringify(tags));
      }
      const { data, status } = await axios.post(
        `${API_BASE}/assistants/${assistantId}/upload-document`,
        form,
        {
          headers: { "Content-Type": "multipart/form-data" },
          onUploadProgress: (evt) => {
            if (!evt.total) return;
            const pct = Math.round((evt.loaded / evt.total) * 100);
            setUploadProgress((p) => ({ ...p, [file.name]: pct }));
          },
          validateStatus: () => true,
        }
      );

      if (status === 402 || (data?.error && /limit/i.test(data.error))) {
        setPlanDialogReason(data?.error || "Plan limit reached.");
        setPlanDialogOpen(true);
        setUploadProgress((p) => {
          const next = { ...p };
          delete next[file.name];
          return next;
        });
        return;
      }

      if (!data?.key) throw new Error("Upload failed");

      const s3Url = data?.s3_url || data?.S3Url || data?.S3URL || null;
      const viewUrl = data?.url || null;
      const downloadUrl = data?.download_url || null;

      const key = data.key;
      setUploadedFiles((prev) => Array.from(new Set([...prev, key])));
      setFileMetaByKey((prev) => ({
        ...prev,
        [key]: {
          ...(prev[key] || {}),
          Key: key,
          Name: file.name,
          Size: file.size,
          UploadedAt: new Date().toISOString(),
          Tags: Array.isArray(tags) ? tags : [],
          S3Url: s3Url || prev[key]?.S3Url || null,
          Url: viewUrl || prev[key]?.Url || null,
          DownloadUrl: downloadUrl || prev[key]?.DownloadUrl || null,
        },
      }));

      setUploadProgress((p) => {
        const next = { ...p, [file.name]: 100 };
        setTimeout(() => {
          setUploadProgress((curr) => {
            const cnext = { ...curr };
            delete cnext[file.name];
            return cnext;
          });
        }, 250);
        return next;
      });

      toast.success(`${file.name} uploaded`);
    } catch {
      toast.error(`Failed to upload ${file.name}`);
      setUploadProgress((p) => {
        const next = { ...p };
        delete next[file.name];
        return next;
      });
    } finally {
      setIsUploading(false);
    }
  };

  const onFileInputChange = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!assistantId) {
      toast.error("No assistant selected");
      return;
    }
    if (files.length === 0) return;
    setSelectedFiles(files);
    const nextTags = {};
    files.forEach((f) => { nextTags[f.name] = selectedFileTags[f.name] || []; });
    setSelectedFileTags(nextTags);
    setTagModalOpen(true);
    e.target.value = "";
  };

  const beginUploadFromModal = async (withTags = true) => {
    if (!assistantId) return toast.error("No assistant selected");
    setTagModalOpen(false);

    if (!withTags) {
      const cleared = {};
      selectedFiles.forEach((f) => { cleared[f.name] = []; });
      setSelectedFileTags((prev) => ({ ...prev, ...cleared }));
    }

    for (const f of selectedFiles) {
      // eslint-disable-next-line no-await-in-loop
      await uploadSingleFile(f);
    }
    setSelectedFiles([]);
    await refreshAll();
    if (autoTrain) await maybeTrainIfNeeded();
  };

  // ---------- URL ingestion ----------
  const parseUrls = (raw = "") =>
    raw
      .split(/\s|,|;|\n/)
      .map((s) => s.trim())
      .filter((s) => s.length > 0);

  const prepareUrlEntries = () => {
    const list = parseUrls(urlInput);
    if (list.length === 0) return toast.error("Please paste at least one URL");

    const valid = list.filter(isLikelyUrl);
    const invalid = list.filter((u) => !isLikelyUrl(u));
    if (invalid.length) {
      toast.warn(`Skipping invalid URL(s): ${invalid.slice(0, 3).join(", ")}${invalid.length > 3 ? "…" : ""}`);
    }
    const deduped = Array.from(new Set(valid));
    setUrlEntries(deduped.map((u, i) => ({ id: `${Date.now()}-${i}`, url: u, tags: [] })));
    setUrlInput("");
  };

  const handleAddUrls = async () => {
    if (!assistantId) return toast.error("No assistant selected");

    const rawList = urlEntries.length ? urlEntries.map(e => e.url) : parseUrls(urlInput);
    if (rawList.length === 0) return toast.error("Please add at least one valid URL");

    const entries = urlEntries.length
      ? urlEntries
      : parseUrls(urlInput).filter(isLikelyUrl).map((u, i) => ({ id: `${Date.now()}-${i}`, url: u, tags: [] }));
    if (entries.length === 0) return toast.error("Please add at least one valid URL");

    setIsAddingUrls(true);
    try {
      let okCount = 0;
      for (const entry of entries) {
        const res = await axios.post(
          `${API_BASE}/assistants/${assistantId}/upload-url`,
          { url: entry.url, tags: entry.tags || [], overwrite: true },
          { validateStatus: () => true }
        );

        if (res.status === 402) {
          setPlanDialogReason(res.data?.error || "Plan limit reached.");
          setPlanDialogOpen(true);
          break;
        }
        if (res.status !== 200 || !res.data?.key) {
          toast.error(`Failed to add: ${entry.url}`);
          continue;
        }

        const {
          key, name, size, tags,
          source_url, url: viewUrl, download_url,
          s3_url, S3Url, S3URL, SourceURL
        } = res.data;

        setUploadedFiles((prev) => Array.from(new Set([...prev, key])));
        setFileMetaByKey((prev) => ({
          ...prev,
          [key]: {
            ...(prev[key] || {}),
            Key: key,
            Name: name || entry.url,
            Size: size || 0,
            UploadedAt: new Date().toISOString(),
            Tags: Array.isArray(tags) ? tags : (entry.tags || []),
            SourceURL: source_url || entry.url || SourceURL, // keep original URL for click-through
            S3Url: s3_url || S3Url || S3URL || prev[key]?.S3Url || null,
            Url: viewUrl || prev[key]?.Url || null,
            DownloadUrl: download_url || prev[key]?.DownloadUrl || null,
          },
        }));
        okCount++;
      }

      if (okCount > 0) {
        toast.success(`Added ${okCount} URL${okCount > 1 ? "s" : ""}`);
      }
      setUrlEntries([]);
      setUrlInput("");
      await refreshAll();
      if (autoTrain) await maybeTrainIfNeeded();
    } catch {
      toast.error("Failed to add URLs");
    } finally {
      setIsAddingUrls(false);
    }
  };

  // ---------- Instructions ----------
  const handleInstructionsSave = async () => {
    if (!assistantId) return toast.error("No assistant selected");
    if (!editorText.trim()) return toast.error("Please write some instructions before saving");

    setForcePendingSinceSave(true);
    setSavingInstructions(true);
    try {
      const { data, status } = await axios.post(
        `${API_BASE}/assistants/${assistantId}/upload-text`,
        { text: editorText },
        { validateStatus: () => true }
      );

      if (status === 402 || (data?.error && /limit/i.test(data.error))) {
        setPlanDialogReason(data?.error || "Plan limit reached.");
        setPlanDialogOpen(true);
        return;
      }

      const key = data?.key;
      if (key) {
        setUploadedFiles((prev) => Array.from(new Set([...prev, key])));
        setFileMetaByKey((prev) => ({
          ...prev,
          [key]: {
            ...(prev[key] || {}),
            Key: key,
            Name: "prompts.txt",
            Size: (data?.upload?.Size) ?? editorText.length,
            UploadedAt: new Date().toISOString(),
          },
        }));
      }

      toast.success("Assistant Instructions saved");
      setHasChangesToTrain(true);

      await refreshAll();

      if (autoTrain) await maybeTrainIfNeeded();
    } catch {
      toast.error("Failed to save Assistant Instructions");
    } finally {
      setForcePendingSinceSave(false);
      setSavingInstructions(false);
    }
  };

  // ---------- Selection / Delete ----------
  const handleToggle = (key) => {
    setCheckedFiles((prev) => prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]);
  };

  const handleDeleteFiles = async () => {
    if (!assistantId) return toast.error("No assistant selected");
    if (checkedFiles.length === 0) return toast.error("Please select at least one item");
    const deletable = checkedFiles.filter((k) => !isProtectedKey(k, fileMetaByKey));
    const skipped = checkedFiles.length - deletable.length;
    if (deletable.length === 0) {
      toast.error("No deletable items selected (admin files are locked).");
      setConfirmDeleteOpen(false);
      return;
    }
    try {
      const res = await axios.post(`${API_BASE}/assistants/${assistantId}/delete-files`, { files: deletable }, { validateStatus: () => true });
      if (res.status === 402) {
        setPlanDialogReason(res.data?.error || "Plan limit reached.");
        setPlanDialogOpen(true);
        return;
      }

      toast.success("Items deleted");
      setUploadedFiles((prev) => prev.filter((k) => !deletable.includes(k)));
      setFileMetaByKey((prev) => {
        const next = { ...prev };
        deletable.forEach((k) => delete next[k]);
        return next;
      });
      setCheckedFiles([]);
      if (skipped > 0) {
        toast.warn(`${skipped} admin item(s) were locked and not deleted.`);
      }
      await refreshAll();
      if (autoTrain) await maybeTrainIfNeeded();
    } catch {
      toast.error("Failed to delete items");
    } finally {
      setConfirmDeleteOpen(false);
    }
  };

  // ---------- Edit tags ----------
  const openTagEditor = (key) => {
    setTagEditKey(key);
    setTagEditValue(fileMetaByKey[key]?.Tags || []);
    setTagEditOpen(true);
  };

  const saveTagsForKey = async () => {
    if (!tagEditKey) return;
    try {
      let res = await axios.put(
        `${API_BASE}/assistants/${assistantId}/files/tags`,
        { key: tagEditKey, tags: tagEditValue, mode: "replace" },
        { validateStatus: () => true }
      );

      if (res.status === 404) {
        res = await axios.post(
          `${API_BASE}/assistants/${assistantId}/files/tags`,
          { key: tagEditKey, tags: tagEditValue },
          { validateStatus: () => true }
        );
      }

      if (res.status === 402) {
        setPlanDialogReason(res.data?.error || "Plan limit reached.");
        setPlanDialogOpen(true);
        return;
      }
      if (res.status !== 200) throw new Error();

      setFileMetaByKey((prev) => ({
        ...prev,
        [tagEditKey]: { ...(prev[tagEditKey] || {}), Tags: tagEditValue }
      }));

      setHasChangesToTrain(true);

      toast.success("Tags updated");
      setTagEditOpen(false);
    } catch {
      toast.error("Failed to update tags");
    }
  };

  // ---------- Train ----------
  const trainAssistant = async () => {
    if (USE_ASYNC_TRAIN) {
      return startAsyncTrain();
    }

    if (!assistantId) return toast.error("No assistant selected");
    if (!hasInstructionFile) {
      toast.warning("Add instructions first (prompts.txt / prompts.json).");
      return;
    }
    setIsTraining(true);
    try {
      const res = await axios.post(`${API_BASE}/assistants/${assistantId}/train`, {}, { validateStatus: () => true });

      if (res.status === 402) {
        setPlanDialogReason(res.data?.error || "Plan limit reached.");
        setPlanDialogOpen(true);
        return;
      }

      const status = (res.data && res.data.status) || "";
      if (status === "success" || status === "noop") {
        setTrainedSnapshotKeys((prev) => [...uploadedFiles]);
        setHasChangesToTrain(false);
        toast.success(status === "success" ? "Training completed!" : "Already up to date.");
        onAssistantReady?.(assistantId);
      } else {
        toast.error("Training failed.");
      }
      await refreshAll();
    } catch {
      toast.error("Error during training.");
    } finally {
      setIsTraining(false);
    }
  };

  const startAsyncTrain = async () => {
    if (!assistantId) return toast.error("No assistant selected");
    if (!hasInstructionFile) {
      toast.warning("Add instructions first (prompts.txt / prompts.json).");
      return;
    }

    setIsTraining(true);
    setTrainStatusError("");
    setTrainStatus(null);
    setTrainJobId(null);
    trainStartedAtRef.current = Date.now();

    try {
      const res = await axios.post(
        `${API_BASE}/assistants/${assistantId}/train-async`,
        {},
        { validateStatus: () => true }
      );

      if (res.status === 402) {
        setPlanDialogReason(res.data?.error || "Plan limit reached.");
        setPlanDialogOpen(true);
        stopTrainingUI();
        return;
      }
      if (res.status !== 202 || !res.data?.job_id) {
        toast.error(res.data?.error || "Failed to enqueue training.");
        stopTrainingUI();
        return;
      }

      const jobId = res.data.job_id;
      setTrainJobId(jobId);
      setTrainStatus("queued");

      clearTrainPolling();
      trainPollHandleRef.current = setInterval(async () => {
        const elapsed = Date.now() - (trainStartedAtRef.current || Date.now());
        if (elapsed > TRAIN_STATUS_TIMEOUT_MS) {
          setTrainStatusError("Training timed out. Please try again.");
          toast.error("Training timed out.");
          stopTrainingUI();
          return;
        }

        try {
          const st = await axios.get(
            `${API_BASE}/assistants/${assistantId}/train-status`,
            { params: { job_id: jobId }, validateStatus: () => true }
          );

          if (st.status === 404) {
            return;
          }
          if (st.status !== 200) {
            setTrainStatusError(st.data?.error || "Unknown status error");
            return;
          }

          const body = st.data || {};
          const s = String(body.status || "").toLowerCase();
          setTrainStatus(s);

          if (s === "completed" || s === "completed_with_errors" || s === "failed") {
            clearTrainPolling();

            if (s === "completed" || s === "completed_with_errors") {
              setTrainedSnapshotKeys((prev) => [...uploadedFiles]);
              setHasChangesToTrain(false);
              await refreshAll();

              if (s === "completed") {
                toast.success("Training completed!");
              } else {
                toast.warn("Training completed with warnings.");
              }
              onAssistantReady?.(assistantId);
            } else {
              toast.error("Training failed.");
            }

            setIsTraining(false);
            setTrainJobId(null);
          }
        } catch (err) {
          setTrainStatusError(err?.message || "Network error while checking status");
        }
      }, TRAIN_STATUS_POLL_MS);
    } catch (e) {
      toast.error("Error starting async training.");
      stopTrainingUI();
    }
  };

  const maybeTrainIfNeeded = async () => {
    const need = computeHasChanges;
    setHasChangesToTrain(need);
    if (need && hasInstructionFile) await trainAssistant();
  };

  const canTrain = hasInstructionFile && (hasChangesToTrain || instructionPending || anyFilePending);

  const goToAssistants = () => navigate("/studio");
  const goToChat = () => {
    if (typeof onGoToChat === "function") onGoToChat();
    else navigate(`/chat?assistantId=${encodeURIComponent(assistantId)}`);
  };

  const pendingChanges = Boolean(hasChangesToTrain || instructionPending || anyFilePending);
  const canActuallyTrain = Boolean(assistantId && hasInstructionFile && pendingChanges && !isTraining);

  const isUpToDate = Boolean(
    assistantId &&
    hasInstructionFile &&
    anyTrained &&
    !pendingChanges &&
    !isTraining
  );

  const btnColor = (!hasInstructionFile || pendingChanges) ? "warning" : "success";
  const btnDisabled = autoTrain || !canActuallyTrain;

  const tooltipText = autoTrain
    ? "Auto-train is ON — uploads & URLs will train automatically."
    : !hasInstructionFile
      ? "Add and save instructions to enable training."
      : isUpToDate
        ? "After making changes, train using this button."
        : "Train your assistant";

  const labelText = isTraining
    ? ( trainStatus ? `Training… (${trainStatus})` : "Training…")
    : !hasInstructionFile
      ? "Add instructions,files,urls to train"
      : pendingChanges
        ? "Train (changes pending)"
        : "Assistant trained";

  const isInstructionsEmpty = (editorText || "").trim().length === 0;
  const trainingBlocked = isInstructionsEmpty && !hasInstructionFile;

  // ---------- UI ----------
  return (
    <>
      {/* Full-screen bootstrap loader */}
      <Backdrop
        open={isBootstrapping}
        sx={{
          color: "text.primary", flexDirection: "column",
          zIndex: (t) => t.zIndex.modal + 4,
          bgcolor: (t) => alpha(t.palette.background.default, 0.92),
        }}
      >
        <Box
          sx={{
            p: 3, borderRadius: 3, border: "1px solid", borderColor: "divider",
            bgcolor: "background.paper", boxShadow: 3, minWidth: 260,
            display: "flex", flexDirection: "column", alignItems: "center"
          }}
        >
          <CircularProgress color="inherit" />
          <Typography variant="h6" sx={{ mt: 2, fontWeight: 800 }}>
            Loading workspace…
          </Typography>
          <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.5 }}>
            Fetching assistant, files, tags & instructions
          </Typography>
        </Box>
      </Backdrop>

      {/* NEW: Full-screen overlay while adding URLs */}
      <Backdrop
        open={isAddingUrls}
        sx={{
          color: "text.primary",
          flexDirection: "column",
          zIndex: (t) => t.zIndex.modal + 5,
          bgcolor: "rgba(0,0,0,0.35)",
        }}
      >
        <Box
          sx={{
            p: 2, borderRadius: 2, border: "1px solid", borderColor: "divider",
            bgcolor: "background.paper", boxShadow: 4, minWidth: 240,
            display: "flex", flexDirection: "column", alignItems: "center"
          }}
        >
          <CircularProgress size={24} />
          <Typography variant="body2" sx={{ mt: 1 }}>
            Adding URL(s)… this can take a moment
          </Typography>
        </Box>
      </Backdrop>

      {!hideHeader && (
        <AppBar
          position="sticky"
          elevation={0}
          color="inherit"
          sx={{ borderBottom: "1px solid", borderColor: "divider", bgcolor: "background.paper" }}
        >
          {(isFetching || isTraining) && <LinearProgress />}
          {isTraining && (trainStatus || trainStatusError) && (
            <Box sx={{ px: 2, py: 1 }}>
              <Alert severity={trainStatus === "failed" ? "error" : (trainStatus === "completed_with_errors" ? "warning" : "info")} variant="outlined">
                {trainStatusError
                  ? `Training: ${trainStatus || "unknown"} — ${trainStatusError}`
                  : `Training: ${trainStatus}`}
              </Alert>
            </Box>
          )}

          <Toolbar sx={{ gap: 1 }}>
            <IconButton edge="start" onClick={goToAssistants}><ArrowBackIcon /></IconButton>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>Training</Typography>
            <Box sx={{ flexGrow: 1 }} />
            <ThemeToggleButton />
            <Tooltip title="Show quick tour">
              <IconButton onClick={startTour}><HelpOutlineIcon /></IconButton>
            </Tooltip>
            <AssistantStatusChip
              anyTrained={anyTrained}
              hasChangesToTrain={hasChangesToTrain}
              isTraining={isTraining}
            />
          </Toolbar>
          {(isFetching || isTraining) && <LinearProgress />}
        </AppBar>
      )}

      <Box sx={{ minHeight: "100vh", bgcolor: "background.default", pb: 10 }}>
        <Container maxWidth="xl" sx={{ pt: 3 }}>
          {/* TOP: Process bar with quick Chat entry */}
          <Paper elevation={0} sx={{ p: 2.5, mb: 2.5, border: "1px solid", borderColor: "divider", position: "sticky", top: hideHeader ? 8 : 72, zIndex: 1, bgcolor: "background.paper" }}>
            <Stack direction={{ xs: "column", sm: "row" }} alignItems={{ xs: "flex-start", sm: "center" }} justifyContent="space-between" spacing={1}>
              <Stepper activeStep={activeStep} alternativeLabel sx={{ flexGrow: 1, minWidth: 260 }}>
                <Step><StepLabel>Upload & Tag</StepLabel></Step>
                <Step><StepLabel>Train</StepLabel></Step>
                <Step><StepLabel>Chat</StepLabel></Step>
              </Stepper>

              {/* Auto-train toggle + info */}
              <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mt: { xs: 1, sm: 0 } }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={autoTrain}
                      onChange={(e) => setAutoTrain(e.target.checked)}
                      size="small"
                    />
                  }
                  label="Auto-train"
                  sx={{ ml: 0 }}
                />
                <Tooltip title="What is Auto-train?">
                  <IconButton size="small" onClick={(e) => setAutoInfoEl(e.currentTarget)}>
                    <InfoOutlinedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Popover
                  open={Boolean(autoInfoEl)}
                  anchorEl={autoInfoEl}
                  onClose={() => setAutoInfoEl(null)}
                  anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                  transformOrigin={{ vertical: "top", horizontal: "right" }}
                  PaperProps={{ sx: { p: 1.25, maxWidth: 320 } }}
                >
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>Auto-train</Typography>
                  <Typography variant="body2" color="text.secondary">
                    When <b>ON</b>, your files and URLs will automatically train after uploading.
                    When <b>OFF</b>, use the floating <i>Train</i> button to apply changes.
                  </Typography>
                </Popover>
              </Stack>

              <Tooltip title="Show quick tour">
                <Button
                  size="small"
                  startIcon={<HelpOutlineIcon />}
                  onClick={startTour}
                  sx={{ borderRadius: 9999 }}
                >
                  Quick tour
                </Button>
              </Tooltip>
              <Button
                size="small"
                variant="outlined"
                sx={{ borderRadius: 9999, px: 1.5, mt: { xs: 1, sm: 0 } }}
                onClick={goToChat}
                disabled={!assistantId || isTraining || hasChangesToTrain}
              >
                Go to Chat
              </Button>
            </Stack>
          </Paper>

          {/* Main layout */}
          <Grid container spacing={2.5} alignItems="stretch">
            {/* LEFT: Sticky Assistant details + Instructions */}
            <Grid item xs={12} md={4} lg={3}>
              <Stack spacing={2.5} sx={{ position: "sticky", top: hideHeader ? 16 : 96 }}>
                <Paper elevation={0} sx={{ p: 2.5, border: "1px solid", borderColor: "divider" }}>
                  <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Assistant Details</Typography>
                    {!isEditingDetails ? (
                      <Tooltip title="Edit">
                        <IconButton size="small" onClick={() => {
                          setDraftName(assistantName);
                          setDraftDesc(assistantDesc);
                          setIsEditingDetails(true);
                        }}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    ) : (
                      <Stack direction="row" spacing={0.5}>
                        <Tooltip title="Save">
                          <span>
                            <IconButton size="small" onClick={saveDetails} disabled={!detailsChanged || savingDetails}>
                              <SaveIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title="Cancel">
                          <IconButton size="small" onClick={() => {
                            setDraftName(assistantName);
                            setDraftDesc(assistantDesc);
                            setIsEditingDetails(false);
                          }}>
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    )}
                  </Stack>

                  {!isEditingDetails ? (
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
                        {assistantName || "Untitled Assistant"}
                      </Typography>
                      {assistantDesc ? (
                        <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: "pre-wrap" }}>
                          {assistantDesc}
                        </Typography>
                      ) : (
                        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic" }}>
                          No description yet.
                        </Typography>
                      )}
                    </Box>
                  ) : (
                    <Box>
                      <TextField
                        label="Name"
                        fullWidth
                        size="small"
                        value={draftName}
                        onChange={(e) => setDraftName(e.target.value)}
                        sx={{ mb: 1.25 }}
                      />
                      <TextField
                        label="Description"
                        fullWidth
                        multiline
                        minRows={5}
                        value={draftDesc}
                        onChange={(e) => setDraftDesc(e.target.value)}
                      />
                      <Stack direction="row" spacing={1} sx={{ mt: 1.25 }}>
                        <Button size="small" variant="contained" onClick={saveDetails} disabled={!detailsChanged || savingDetails}>
                          {savingDetails ? "Saving…" : "Save"}
                        </Button>
                        <Button size="small" variant="text" onClick={() => {
                          setDraftName(assistantName);
                          setDraftDesc(assistantDesc);
                          setIsEditingDetails(false);
                        }}>
                          Cancel
                        </Button>
                      </Stack>
                    </Box>
                  )}
                </Paper>

                {/* Assistant Instructions */}
                <Paper elevation={0} sx={{ p: 2.5, border: "1px solid", borderColor: "divider" }}>
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Assistant Instructions</Typography>
                    <Box sx={{ flexGrow: 1 }} />
                    {instructionKey ? (
                      instructionPending ? (
                        <Chip size="small" icon={<PendingIcon />} color="warning" label="Pending" />
                      ) : (
                        <Chip size="small" icon={<CheckCircleIcon />} color="success" label="Trained" />
                      )
                    ) : (
                      <Chip size="small" color="error" variant="outlined" label="Required" sx={{ fontWeight: 600 }} />
                    )}
                    <Tooltip title="Expand editor">
                      <IconButton size="small" onClick={() => setInstructionsModalOpen(true)}>
                        <OpenInFullIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Stack>

                  {trainingBlocked && (
                    <Alert severity="warning" sx={{ mb: 1.25 }}>
                      Instructions are <b>mandatory</b> to train an assistant. Please add and save them first.
                    </Alert>
                  )}

                  <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
                    <Button size="small" variant="outlined" onClick={() => {
                      const text = `# Role
You are "Nova", a concise, helpful assistant for our product.

# Domain Knowledge
- Use only the uploaded files as ground truth.
- If unsure, ask clarifying questions.

# Tone
- Friendly, professional, brief.

# Output Rules
- Bullet points when listing.
- Include cited filenames when relevant.
- Prefer answers that mention relevant tags when known (e.g., "Pricing", "HR").`;
                      setEditorText(text);
                      toast.info("Inserted example instructions. Edit then click Save.");
                    }}>
                      Insert Example
                    </Button>
                    <Button size="small" variant="text" onClick={() => {
                      const text = `# Role
You are a Tier-1 support agent for Acme.

# What to do
- Diagnose the issue.
- Offer 2–3 next steps.
- Link to relevant docs by filename & tags.

# Style
- Empathetic and calm.
- Avoid jargon.`;
                      setEditorText(text);
                      toast.info("Inserted support template. Edit then click Save.");
                    }}>
                      Support Template
                    </Button>
                  </Stack>

                  <TextField
                    label={
                      <span>
                        Write your assistant instructions <span style={{ color: theme.palette.error.main }}>*</span>
                      </span>
                    }
                    placeholder="Describe the role, domain guardrails, tone, and output rules…"
                    multiline
                    rows={10}
                    fullWidth
                    variant="outlined"
                    value={editorText}
                    onChange={(e) => setEditorText(e.target.value)}
                    inputRef={instructionsRef}
                    error={trainingBlocked}
                    helperText={
                      trainingBlocked
                        ? "This field is mandatory. Add instructions to enable training."
                        : "Tip: Be specific about role, sources, tone, and formatting."
                    }
                  />
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1.25 }}>
                    <Button
                      size="small"
                      variant="contained"
                      onClick={handleInstructionsSave}
                      disabled={savingInstructions || loadingInstructions || isInstructionsEmpty}
                    >
                      {savingInstructions ? "Saving…" : "Save Instructions"}
                    </Button>
                    {loadingInstructions && <CircularProgress size={18} />}
                  </Stack>
                </Paper>
              </Stack>
            </Grid>

            {/* RIGHT: Uploaders + lists */}
            <Grid item xs={12} md={8} lg={9}>
              {/*Advance Integration*/}
              {/* ================= ADVANCED INTEGRATIONS (PLATINUM) WRAPPER ================= */}
{assistantId && (
  <Paper
    elevation={0}
    sx={{
      mb: 3,
      borderRadius: 3,
      border: "1px solid",
      borderColor: (t) => t.palette.divider,
      bgcolor: (t) => t.palette.background.paper,
    }}
  >
    <Accordion
      defaultExpanded={userPlan === "platinum"}
      disableGutters
      sx={{
        "&:before": { display: "none" },
        boxShadow: "none",
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        sx={{
          borderBottom: "1px solid",
          borderColor: (t) => t.palette.divider,
          minHeight: 64,
          bgcolor: (t) => (userPlan === "platinum" ? alpha(t.palette.warning.main, 0.08) : "transparent"),
          "& .MuiAccordionSummary-content": { my: 1 },
        }}
      >
        <Stack direction="row" spacing={1.5} alignItems="center">
          <BoltIcon sx={{ color: (t) => t.palette.warning.dark }} />
          <Typography
            variant="h6"
            sx={{
              fontWeight: 900,
              letterSpacing: 0.5,
              color: (t) => (userPlan === "platinum" ? t.palette.warning.dark : t.palette.text.primary),
            }}
          >
            ADVANCED INTEGRATIONS
          </Typography>
          <Chip
            label="PLATINUM"
            size="small"
            sx={{
              fontWeight: 700,
              color: "white",
              bgcolor: (t) =>
                userPlan === "platinum" ? t.palette.warning.main : t.palette.grey[500],
              borderRadius: 1,
              px: 0.5,
              textTransform: "uppercase",
            }}
          />
        </Stack>
      </AccordionSummary>

      <AccordionDetails sx={{ p: 0 }}>
        <AdvancedIntegrationsSection userPlan={userPlan} />
      </AccordionDetails>
    </Accordion>
  </Paper>
)}

              {/* Upload Files (accordion) */}
              <Accordion defaultExpanded={true} disableGutters sx={{ mb: 2.5, border: "1px solid", borderColor: "divider", borderRadius: 2 }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />} ref={uploadRef}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <CloudUploadIcon color="primary" />
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Upload Files</Typography>
                    {isUploading && <Chip size="small" label="Uploading…" sx={{ ml: 1 }} color="primary" variant="outlined" />}
                  </Stack>
                </AccordionSummary>
                <AccordionDetails>
                  <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ xs: "stretch", sm: "center" }} sx={{ mb: 1.5 }}>
                    <input
                      id="file-input"
                      type="file"
                      multiple
                      onChange={onFileInputChange}
                      style={{ display: "none" }}
                    />
                    <label htmlFor="file-input">
                      <Button variant="outlined" component="span">Choose Files</Button>
                    </label>
                  </Stack>

                  {/* Active upload progress */}
                  {Object.keys(uploadProgress).length > 0 && (
                    <Box sx={{ mt: 1.5 }}>
                      <Divider sx={{ mb: 1 }} />
                      <Stack spacing={1.2}>
                        {Object.entries(uploadProgress).map(([fname, pct]) => (
                          <Box key={fname}>
                            <Typography variant="caption" sx={{ display: "block", mb: 0.5, color: "text.secondary" }}>
                              {fname} — {pct}%
                            </Typography>
                            <LinearProgress variant="determinate" value={Number(pct) || 0} sx={{ height: 8, borderRadius: 1 }} />
                          </Box>
                        ))}
                      </Stack>
                    </Box>
                  )}

                  {/* Supported types (no plan-specific locks, no numbers) */}
                  <Box sx={{ mt: 1.5 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
                      Supported
                    </Typography>

                    <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap">
                      {/* Documents */}
                      <Chip label="PDF" size="small" variant="outlined" />
                      <Chip label="DOCX" size="small" variant="outlined" />
                      <Chip label="TXT" size="small" variant="outlined" />
                      <Chip label="Markdown" size="small" variant="outlined" />

                      {/* Spreadsheets */}
                      <Chip label="CSV" size="small" variant="outlined" />
                      <Chip label="TSV" size="small" variant="outlined" />
                      <Chip label="XLSX" size="small" variant="outlined" />

                      {/* Media */}
                      <Chip label="Images" size="small" variant="outlined" />
                      <Chip label="Audio/Video" size="small" variant="outlined" />

                      {/* Web */}
                      <Chip label="HTML" size="small" variant="outlined" />
                    </Stack>
                  </Box>
                </AccordionDetails>
              </Accordion>

              {/* Uploaded Files list */}
              <Paper
                elevation={0}
                sx={{
                  border: "1px solid",
                  borderColor: "divider",
                  display: "flex",
                  flexDirection: "column",
                  minHeight: 260,
                  mb: 2.5,
                }}
                ref={filesRef}
              >
                <Box sx={{ p: 1.5, pb: 0 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 800, display: "flex", alignItems: "center", gap: 0.5 }}>
                    <DescriptionIcon /> Uploaded Files ({filteredDocs.length})
                  </Typography>
                </Box>

                <Stack direction="row" spacing={1} alignItems="center" sx={{ px: 2, py: 1 }}>
                  <TextField
                    size="small"
                    placeholder="Search files & tags…"
                    value={fileQuery}
                    onChange={(e) => setFileQuery(e.target.value)}
                    sx={{ minWidth: 260 }}
                    InputProps={{ startAdornment: (<InputAdornment position="start"><SearchIcon /></InputAdornment>) }}
                  />
                  <Box sx={{ flexGrow: 1 }} />
                  <Button variant="outlined" onClick={toggleFilesPage} disabled={pageDocKeys.length === 0}>
                    { (pageDocKeys.length && filesPageAllChecked) ? "Unselect Page" : "Select Page" }
                  </Button>
                  <Button variant="outlined" startIcon={<ClearIcon />} onClick={() => setCheckedFiles([])} disabled={checkedFiles.length === 0}>
                    Clear Selection
                  </Button>
                  <Button
                    variant="contained"
                    color="error"
                    startIcon={<DeleteOutlineIcon />}
                    onClick={() => setConfirmDeleteOpen(true)}
                    disabled={checkedFiles.filter((k) => !isProtectedKey(k, fileMetaByKey)).length === 0}
                  >
                    Delete Selected
                  </Button>
                  <Chip size="small" label={`${checkedFiles.length} selected`} variant="outlined" sx={{ ml: 1 }} />
                </Stack>

                <Divider />

                <Box sx={{ flex: 1, overflowY: "auto", px: 1.5, py: 1 }}>
                  {filesTotal > 0 ? (
                    <>
                      <List dense>
                        {pageDocKeys.map((key, index) => {
                          const checked = checkedFiles.includes(key);
                          const name = fileMetaByKey[key]?.Name || key.split("/").pop();
                          const meta = fileMetaByKey[key] || {};
                          const used = !!meta.UsedInTrainingAt;
                          const tags = meta.Tags || [];
                          const kind = getKindFromExt(name || key);

                          return (
                            <ListItem
                              key={`${key}-${index}`}
                              name={`${name}`}
                              sx={{
                                borderRadius: 1,
                                "&:hover": { bgcolor: "action.hover" },
                                pr: 9
                              }}
                              secondaryAction={
                                <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mr: 0.5 }}>
                                  <Tooltip title="View">
                                    <IconButton edge="end" onClick={() => openViewerForKey(key)}>
                                      <VisibilityIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title="Edit tags">
                                    <IconButton edge="end" onClick={() => openTagEditor(key)} disabled={isProtectedKey(key, fileMetaByKey)}>
                                      <EditIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                </Stack>
                              }
                            >
                              <Checkbox
                                edge="start"
                                tabIndex={-1}
                                checked={checked}
                                disabled={isProtectedKey(key, fileMetaByKey)}
                                onChange={() => !isProtectedKey(key, fileMetaByKey) && handleToggle(key)}
                              />
                              <KindIcon kind={kind} />
                              <ListItemText
                                primaryTypographyProps={{ noWrap: true }}
                                onClick={() => !isProtectedKey(key, fileMetaByKey) && handleToggle(key)}
                                primary={
                                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
                                    <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>{name}</span>
                                    <Chip size="small" label={kindLabel(kind)} variant="outlined" sx={{ height: 22 }} />
                                    {used ? (
                                      <Chip size="small" icon={<CheckCircleIcon />} color="success" label="Trained" sx={{ height: 22 }} />
                                    ) : (
                                      <Chip size="small" icon={<PendingIcon />} color="warning" label="Pending" sx={{ height: 22 }} />
                                    )}
                                    {isProtectedKey(key, fileMetaByKey) && (
                                      <Chip
                                        size="small"
                                        icon={<LockIcon />}
                                        label="Admin"
                                        variant="outlined"
                                        color="info"
                                        sx={{ height: 22 }}
                                      />
                                    )}
                                  </Box>
                                }
                                secondary={
                                  <Stack spacing={0.5} sx={{ mt: 0.25 }}>
                                    {tags.length > 0 ? (
                                      <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap">
                                        {tags.map((t, i) => (
                                          <Chip key={`${t}-${i}`} size="small" label={t} variant="outlined" />
                                        ))}
                                      </Stack>
                                    ) : (
                                      <Typography variant="caption" color="text.secondary" sx={{ fontStyle: "italic" }}>
                                        No tags
                                      </Typography>
                                    )}
                                  </Stack>
                                }
                              />
                            </ListItem>
                          );
                        })}
                      </List>

                      <Box sx={{ mt: 1, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2, flexWrap: "wrap" }}>
                        <Typography variant="caption" color="text.secondary">
                          Showing <b>{filesStart + 1}</b>–<b>{filesEnd}</b> of <b>{filesTotal}</b> files
                        </Typography>
                        <Pagination
                          count={filesPageCount}
                          page={filesPage}
                          onChange={(_e, v) => setFilesPage(v)}
                          color="primary"
                          size="small"
                        />
                      </Box>
                    </>
                  ) : (
                    !isFetching && <Typography color="text.secondary">No files added yet.</Typography>
                  )}
                </Box>
              </Paper>

              {/* Add URLs (accordion) */}
              <Accordion defaultExpanded={true} disableGutters sx={{ mb: 2.5, border: "1px solid", borderColor: "divider", borderRadius: 2 }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <LinkIcon color="primary" />
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Add URLs</Typography>
                    <Chip size="small" label="YouTube supported" color="success" variant="outlined" />
                    {isAddingUrls && (
                      <Chip size="small" label="Adding…" sx={{ ml: 1 }} color="primary" variant="outlined" />
                    )}
                  </Stack>
                </AccordionSummary>

                <AccordionDetails>
                  <Stack direction={{ xs: "column", md: "row" }} spacing={1} sx={{ mb: 1.5 }}>
                    <TextField
                      fullWidth
                      size="small"
                      placeholder="Paste URL(s)… (space/comma/newline separated)"
                      value={urlInput}
                      onChange={(e) => setUrlInput(e.target.value)}
                      InputProps={{
                        startAdornment: (<InputAdornment position="start"><LinkIcon /></InputAdornment>),
                      }}
                    />
                    <Button
                      variant="outlined"
                      onClick={prepareUrlEntries}
                      disabled={!urlInput.trim()}
                    >
                      Review & Tag
                    </Button>
                    <Button
                      variant="contained"
                      onClick={handleAddUrls}
                      disabled={isAddingUrls || (urlEntries.length === 0 && !urlInput.trim())}
                    >
                      {isAddingUrls ? "Adding…" : "Add URL(s)"}
                    </Button>
                  </Stack>

                  {/* Supported URL types (compact) */}
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
                      Supported (public URLs only)
                    </Typography>
                    <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap">
                      <Chip label="Web pages (http/https)" size="small" variant="outlined" />
                      <Chip label="PDF / DOCX" size="small" variant="outlined" />
                      <Chip label="TXT / Markdown" size="small" variant="outlined" />
                      <Chip label="Knowledge-base / Docs" size="small" variant="outlined" />
                      <Chip label="YouTube" size="small" variant="outlined" />
                    </Stack>
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
                      Private or login-protected pages aren’t supported. Use file uploads for gated content.
                    </Typography>
                  </Box>

                  {/* Per-URL tagging list */}
                  {urlEntries.length > 0 && (
                    <Box sx={{ border: "1px dashed", borderColor: "divider", borderRadius: 2, p: 1.5, mt: 1.25 }}>
                      <Typography variant="subtitle2" sx={{ mb: 1 }}>Assign tags per URL (optional)</Typography>
                      <Stack spacing={1}>
                        {urlEntries.map((e, i) => (
                          <Box key={e.id}>
                            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
                              <Typography variant="body2" sx={{ fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                {e.url}
                              </Typography>
                              <Box sx={{ flexGrow: 1 }} />
                              <Button size="small" variant="text" color="inherit" startIcon={<ClearIcon />} onClick={() => setUrlEntries((prev) => prev.filter((x) => x.id !== e.id))}>
                                Remove
                              </Button>
                            </Stack>
                            <TagInput
                              value={e.tags}
                              onChange={(tags) => setUrlEntries((prev) => prev.map((x) => (x.id === e.id ? { ...x, tags } : x)))}
                              placeholder="Add a tag and press Enter (e.g., Docs, Marketing, API)"
                            />
                          </Box>
                        ))}
                      </Stack>
                      <Stack direction="row" spacing={1} sx={{ mt: 1.25 }}>
                        <Button
                          size="small"
                          variant="contained"
                          onClick={handleAddUrls}
                          disabled={isAddingUrls || urlEntries.length === 0}
                        >
                          {isAddingUrls ? "Uploading…" : `Upload ${urlEntries.length} URL${urlEntries.length > 1 ? "s" : ""}`}
                        </Button>
                        <Button size="small" variant="text" onClick={() => setUrlEntries([])}>Clear list</Button>
                      </Stack>
                    </Box>
                  )}
                </AccordionDetails>
              </Accordion>

              {/* Uploaded URLs list */}
              <Paper
                elevation={0}
                sx={{
                  border: "1px solid",
                  borderColor: "divider",
                  display: "flex",
                  flexDirection: "column",
                  minHeight: 260,
                }}
              >
                <Box sx={{ p: 1.5, pb: 0 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 800, display: "flex", alignItems: "center", gap: 0.5 }}>
                    <LinkIcon /> Uploaded URLs ({filteredUrls.length})
                  </Typography>
                </Box>

                <Stack direction="row" spacing={1} alignItems="center" sx={{ px: 2, py: 1 }}>
                  <TextField
                    size="small"
                    placeholder="Search URLs & tags…"
                    value={urlQuery}
                    onChange={(e) => setUrlQuery(e.target.value)}
                    sx={{ minWidth: 260 }}
                    InputProps={{ startAdornment: (<InputAdornment position="start"><SearchIcon /></InputAdornment>) }}
                  />
                  <Box sx={{ flexGrow: 1 }} />
                  <Button variant="outlined" onClick={toggleUrlsPage} disabled={pageUrlKeys.length === 0}>
                    { (pageUrlKeys.length && urlsPageAllChecked) ? "Unselect Page" : "Select Page" }
                  </Button>
                  <Button variant="outlined" startIcon={<ClearIcon />} onClick={() => setCheckedFiles([])} disabled={checkedFiles.length === 0}>
                    Clear Selection
                  </Button>
                  <Button
                    variant="contained"
                    color="error"
                    startIcon={<DeleteOutlineIcon />}
                    onClick={() => setConfirmDeleteOpen(true)}
                    disabled={checkedFiles.length === 0}
                  >
                    Delete Selected
                  </Button>
                  <Chip size="small" label={`${checkedFiles.length} selected`} variant="outlined" sx={{ ml: 1 }} />
                </Stack>

                <Divider />

                <Box sx={{ flex: 1, overflowY: "auto", px: 1.5, py: 1 }}>
                  {urlsTotal > 0 ? (
                    <>
                      <List dense>
                        {pageUrlKeys.map((key, index) => {
                          const checked = checkedFiles.includes(key);
                          const name = fileMetaByKey[key]?.Name || key.split("/").pop();
                          const meta = fileMetaByKey[key] || {};
                          const used = !!meta.UsedInTrainingAt;
                          const tags = meta.Tags || [];
                          const sourceHref = meta.SourceURL || (isLikelyUrl(name) ? name : null);

                          return (
                            <ListItem
                              key={`${key}-${index}`}
                              sx={{
                                borderRadius: 1,
                                "&:hover": { bgcolor: "action.hover" },
                                pr: 9
                              }}
                              secondaryAction={
                                <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mr: 0.5 }}>
                                  {!!sourceHref && (
                                    <Tooltip title="Open source URL">
                                      <IconButton edge="end" component="a" href={sourceHref} target="_blank" rel="noreferrer">
                                        <OpenInNewIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                  )}
                                  <Tooltip title="Edit tags">
                                    <IconButton edge="end" onClick={() => openTagEditor(key)}>
                                      <EditIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                </Stack>
                              }
                            >
                              <Checkbox edge="start" tabIndex={-1} checked={checked} onChange={() => handleToggle(key)} />
                              <LinkIcon sx={{ mr: 1, color: "text.secondary" }} />
                              <ListItemText
                                primaryTypographyProps={{ noWrap: true }}
                                onClick={() => handleToggle(key)}
                                primary={
                                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
                                    {sourceHref ? (
                                      <MUILink href={sourceHref} target="_blank" rel="noreferrer" underline="hover" sx={{ overflow: "hidden", textOverflow: "ellipsis" }}>
                                        {sourceHref}
                                      </MUILink>
                                    ) : (
                                      <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>{name}</span>
                                    )}
                                    {used ? (
                                      <Chip size="small" icon={<CheckCircleIcon />} color="success" label="Trained" sx={{ height: 22 }} />
                                    ) : (
                                      <Chip size="small" icon={<PendingIcon />} color="warning" label="Pending" sx={{ height: 22 }} />
                                    )}
                                  </Box>
                                }
                                secondary={
                                  <Stack spacing={0.5} sx={{ mt: 0.25 }}>
                                    {tags.length > 0 ? (
                                      <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap">
                                        {tags.map((t, i) => (
                                          <Chip key={`${t}-${i}`} size="small" label={t} variant="outlined" />
                                        ))}
                                      </Stack>
                                    ) : (
                                      <Typography variant="caption" color="text.secondary" sx={{ fontStyle: "italic" }}>
                                        No tags
                                      </Typography>
                                    )}
                                  </Stack>
                                }
                              />
                            </ListItem>
                          );
                        })}
                      </List>

                      <Box sx={{ mt: 1, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2, flexWrap: "wrap" }}>
                        <Typography variant="caption" color="text.secondary">
                          Showing <b>{urlsStart + 1}</b>–<b>{urlsEnd}</b> of <b>{urlsTotal}</b> URLs
                        </Typography>
                        <Pagination
                          count={urlsPageCount}
                          page={urlsPage}
                          onChange={(_e, v) => setUrlsPage(v)}
                          color="primary"
                          size="small"
                        />
                      </Box>
                    </>
                  ) : (
                    !isFetching && <Typography color="text.secondary">No URLs added yet.</Typography>
                  )}
                </Box>
              </Paper>
            </Grid>
          </Grid>

          {/* Delete confirmation */}
          <Dialog open={confirmDeleteOpen} onClose={() => setConfirmDeleteOpen(false)}>
            <DialogTitle>Delete selected items?</DialogTitle>
            <DialogContent>
              <Typography variant="body2" color="text.secondary">
                This cannot be undone. {checkedFiles.length} item(s) will be permanently removed. Admin files are locked and will be skipped.
              </Typography>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setConfirmDeleteOpen(false)}>Cancel</Button>
              <Button variant="contained" color="error" onClick={handleDeleteFiles}>Delete</Button>
            </DialogActions>
          </Dialog>

          {/* Edit tags dialog */}
          <Dialog open={tagEditOpen} onClose={() => setTagEditOpen(false)} fullWidth maxWidth="sm">
            <DialogTitle>Edit tags</DialogTitle>
            <DialogContent dividers>
              <Typography variant="body2" sx={{ mb: 1 }}>
                File Name: <b style={{ wordBreak: "break-all" }}>{tagEditKey}</b>
              </Typography>
              <TagInput value={tagEditValue} onChange={setTagEditValue} />
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setTagEditOpen(false)}>Cancel</Button>
              <Button variant="contained" onClick={saveTagsForKey}>Save</Button>
            </DialogActions>
          </Dialog>

          {/* Plan upgrade dialog */}
          <PlanLimitDialog
            open={planDialogOpen}
            onClose={() => setPlanDialogOpen(false)}
            reason={planDialogReason}
          />
        </Container>
      </Box>

      {/* Floating Train FAB */}
      <Box ref={trainFabRef}>
        <Tooltip
          title={
            !hasInstructionFile
              ? "Add and save instructions to enable training"
              : hasChangesToTrain
              ? "Train with current files, URLs & tags"
              : "Already up to date"
          }
        >
          <span>
            <Badge
              color="warning"
              overlap="circular"
              badgeContent={pendingCount > 0 ? pendingCount : null}
              invisible={!hasChangesToTrain}
              anchorOrigin={{ vertical: "top", horizontal: "left" }}
              sx={{ position: "fixed", right: 24, bottom: 92, zIndex: (t) => t.zIndex.fab + 1 }}
            >
              <Tooltip title={tooltipText}>
                <span>
                  <Fab
                    variant="extended"
                    onClick={trainAssistant}
                    disabled={btnDisabled}
                    color={btnColor}
                    sx={{
                      position: "fixed",
                      right: 24,
                      bottom: 24,
                      zIndex: (t) => t.zIndex.fab + 1,
                      boxShadow: 6,
                      ...(isUpToDate && {
                        "&.Mui-disabled": {
                          bgcolor: (t) => t.palette.success.main,
                          color: (t) => t.palette.success.contrastText,
                          opacity: 1,
                        },
                      }),
                      ...(!hasInstructionFile && {
                        "&.Mui-disabled": {
                          bgcolor: (t) => t.palette.warning.main,
                          color: (t) => t.palette.getContrastText(t.palette.warning.main),
                          opacity: 1,
                        },
                      }),
                    }}
                  >
                    <BoltIcon sx={{ mr: 1 }} />
                    {labelText}
                  </Fab>
                </span>
              </Tooltip>
            </Badge>
          </span>
        </Tooltip>
      </Box>

      {/* Instructions Modal */}
      <Dialog
        open={instructionsModalOpen}
        onClose={() => setInstructionsModalOpen(false)}
        fullWidth
        maxWidth="lg"
        fullScreen={fullScreen}
        PaperProps={{ sx: { m: 0 } }}
      >
        <DialogTitle sx={{ pr: 1.5 }}>
          Assistant Instructions
          <Box sx={{ float: "right" }}>
            <Tooltip title="Collapse">
              <IconButton onClick={() => setInstructionsModalOpen(false)}>
                <CloseFullscreenIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </DialogTitle>
        <DialogContent dividers sx={{ pt: 1.5 }}>
          <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
            <Button size="small" variant="outlined" onClick={() => {
              const text = `# Role
You are "Nova", a concise, helpful assistant for our product.

# Domain Knowledge
- Use only the uploaded files as ground truth.
- If unsure, ask clarifying questions.

# Tone
- Friendly, professional, brief.

# Output Rules
- Bullet points when listing.
- Include cited filenames when relevant.
- Prefer answers that mention relevant tags when known (e.g., "Pricing", "HR").`;
              setEditorText(text);
              toast.info("Inserted example instructions. Edit then click Save.");
            }}>
              Insert Example
            </Button>
            <Button size="small" variant="text" onClick={() => {
              const text = `# Role
You are a Tier-1 support agent for Acme.

# What to do
- Diagnose the issue.
- Offer 2–3 next steps.
- Link to relevant docs by filename & tags.

# Style
- Empathetic and calm.
- Avoid jargon.`;
              setEditorText(text);
              toast.info("Inserted support template. Edit then click Save.");
            }}>
              Support Template
            </Button>
          </Stack>

          <TextField
            label={
              <span>
                Write your assistant instructions <span style={{ color: theme.palette.error.main }}>*</span>
              </span>
            }
            placeholder="Describe the role, domain guardrails, tone, and output rules…"
            multiline
            rows={16}
            fullWidth
            variant="outlined"
            value={editorText}
            onChange={(e) => setEditorText(e.target.value)}
            inputRef={instructionsRef}
            error={trainingBlocked}
            helperText={
              trainingBlocked
                ? "This field is mandatory. Add instructions to enable training."
                : "Tip: Be specific about role, sources, tone, and formatting."
            }
          />

          {instructionKey && (
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
              Current file on server: <b>{instructionKey.split("/").pop()}</b>
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInstructionsModalOpen(false)}>Close</Button>
          <Button
            variant="contained"
            onClick={handleInstructionsSave}
            disabled={savingInstructions || loadingInstructions || isInstructionsEmpty}
          >
            {savingInstructions ? "Saving…" : "Save Instructions"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Tagging Modal */}
      <Dialog
        open={tagModalOpen}
        onClose={() => setTagModalOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Tag your files (optional)</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" sx={{ mb: 1.5 }} color="text.secondary">
            Add tags so answers can filter/cite by topic. You can also skip to upload without tags.
          </Typography>
          <Stack spacing={1.25}>
            {selectedFiles.map((f, i) => (
              <Box key={`${f.name}-${i}`} sx={{ p: 1, border: "1px dashed", borderColor: "divider", borderRadius: 1.5 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.75 }}>{f.name}</Typography>
                <TagInput
                  value={selectedFileTags[f.name] || []}
                  onChange={(tags) => setSelectedFileTags((prev) => ({ ...prev, [f.name]: tags }))}
                  placeholder="Add a tag and press Enter (e.g., Pricing, Onboarding, HR)"
                />
              </Box>
            ))}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => beginUploadFromModal(false)}>Skip & Upload</Button>
          <Button variant="contained" onClick={() => beginUploadFromModal(true)}>Upload</Button>
        </DialogActions>
      </Dialog>

      {/* Guided tour coachmark */}
      <Coachmark
        open={tour.open}
        anchorEl={tourSteps[tour.step].ref?.current}
        title={tourSteps[tour.step].title}
        body={tourSteps[tour.step].body}
        step={tour.step}
        total={tourSteps.length}
        onNext={() => setTour((t) => ({ ...t, step: Math.min(t.step + 1, tourSteps.length - 1) }))}
        onPrev={() => setTour((t) => ({ ...t, step: Math.max(t.step - 1, 0) }))}
        onSkip={endTour}
        onDone={endTour}
      />

      {/* Viewers */}
      <DocumentViewer
        open={viewerOpen}
        onClose={() => setViewerOpen(false)}
        fileUrl={viewer.url}
        fileName={viewer.name}
        contentType={viewer.contentType}
      />

      <UniversalDocumentViewer
        open={viewerOpen}
        onClose={() => setViewerOpen(false)}
        fileUrl={viewer.url}
        fileName={viewer.name}
        contentType={viewer.contentType}
      />
    </>
  );
};

export default TrainingInterface;
